import { useState, useEffect } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { obtenerSlugDeSubdominio } from '../lib/subdominio'
import './Auth.css'

export default function Auth({ onLogin, invitacionPendiente, nombrePrecargado }) {
  // Si viene de un link de invitación, arranca en "Registrarse" (caso más
  // común) — pero el toggle a "Ingresar" sigue disponible tal cual, para
  // quien ya tiene cuenta y solo necesita loguearse para redimir el código.
  const [modo, setModo] = useState(invitacionPendiente ? 'registro' : 'login') // 'login' | 'registro' | 'recuperar'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mostrarPassword, setMostrarPassword] = useState(false)
  const [nombre, setNombre] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [mensaje, setMensaje] = useState('')
  const [marca, setMarca] = useState(null) // { nombre, color_primario, color_secundario } del workspace del subdominio, si hay

  // #7: nombrePrecargado llega async (la RPC en App.js corre después del
  // primer render de Auth) — solo pisa el campo si el usuario no tipeó nada
  // todavía, queda editable por si quiere corregirlo.
  useEffect(() => {
    if (nombrePrecargado && !nombre) setNombre(nombrePrecargado)
  }, [nombrePrecargado])

  useEffect(() => {
    const slug = obtenerSlugDeSubdominio()
    if (!slug) return
    supabase.rpc('resolver_workspace_por_slug', { p_slug: slug }).then(({ data }) => {
      if (data && data.length > 0) {
        const ws = data[0]
        setMarca(ws)
        document.documentElement.style.setProperty('--verde-dark', ws.color_primario || '#5b7fbc')
        document.documentElement.style.setProperty('--verde', ws.color_secundario || '#dac2da')
      }
      // Si no hay fila (no existe o está suspendido), se queda con el
      // branding genérico de Juco — no es un error, no hace falta avisar.
    })
  }, [])

  const handleLogin = async () => {
    if (!email || !password) { setError('Completá email y contraseña'); return }
    setLoading(true); setError('')
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) {
      setError(error.code === 'email_not_confirmed'
        ? 'Todavía no confirmaste tu cuenta — revisá tu correo'
        : 'Email o contraseña incorrectos')
      setLoading(false)
      return
    }
    onLogin(data.user)
    setLoading(false)
  }

  const handleRegistro = async () => {
    if (!email || !password || !nombre) { setError('Completá todos los campos'); return }
    if (password.length < 6) { setError('La contraseña debe tener al menos 6 caracteres'); return }
    setLoading(true); setError('')

    // El allowlist de Redirect URLs de Supabase Auth solo tiene el dominio
    // pelado (juco.app) — un emailRedirectTo apuntando a un subdominio de
    // workspace (ej. prueba.juco.app) lo rechaza en silencio, sin error
    // visible, y cae al Site URL pelado sin el ?invitacion= (confirmado
    // contra el proyecto real vía /auth/v1/verify: un redirect_to con
    // subdominio se reemplaza por "https://juco.app" a secas; el mismo
    // path+query sobre el dominio pelado sí se preserva). Se fuerza acá el
    // hostname a juco.app conservando path+query, para que el código de
    // invitación sobreviva la confirmación de mail sin depender de en qué
    // subdominio arrancó el registro.
    const redirectUrl = new URL(window.location.href)
    redirectUrl.hostname = 'juco.app'

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { nombre }, emailRedirectTo: redirectUrl.toString() }
    })

    if (error) {
      // 'over_email_send_rate_limit' es el cooldown normal de Supabase tras
      // un signup sin confirmar (60s en prod) — no es una falla real de
      // registro, y "Intentá de nuevo" ahí es engañoso.
      setError(error.code === 'over_email_send_rate_limit'
        ? 'Ya te mandamos un mail de confirmación hace poco. Esperá un minuto y volvé a intentar.'
        : 'No se pudo crear la cuenta. Intentá de nuevo.')
      setLoading(false)
      return
    }

    // Guardar nombre en tabla usuarios
    if (data.user) {
      await supabase.from('usuarios').upsert({
        id: data.user.id,
        email,
        nombre
      })
    }

    setMensaje(`Te mandamos un mail a ${email} para confirmar tu cuenta — confirmalo antes de iniciar sesión.`)
    setLoading(false)
  }

  const handleRecuperar = async () => {
    if (!email) { setError('Ingresá tu email'); return }
    setLoading(true); setError('')

    // Mismo motivo que en handleRegistro: el allowlist de Redirect URLs
    // solo tiene el dominio pelado, un redirectTo con subdominio de
    // workspace cae en silencio al Site URL — funciona igual (ActualizarPassword
    // no depende de ningún query param), pero se fuerza acá también para
    // no dejar un caso más sujeto al mismo comportamiento silencioso.
    const redirectUrl = new URL(window.location.origin)
    redirectUrl.hostname = 'juco.app'
    await supabase.auth.resetPasswordForEmail(email, { redirectTo: redirectUrl.toString() })

    // Mensaje neutro a propósito: no confirma ni niega si el email existe
    // en la base, para no habilitar enumeración de usuarios. Se muestra
    // igual haya o no haya cuenta con ese email.
    setMensaje('Si ese email tiene una cuenta, te enviamos un link para restablecer tu contraseña. Revisá tu casilla.')
    setLoading(false)
  }

  return (
    <div className="auth-page">
      <div className="auth-hero">
        {!marca && <img src="/logo-transparente.png" alt="Juco" className="auth-logo-img" />}
        <h1 className="auth-titulo">{marca?.nombre || 'Juco'}</h1>
        <p className="auth-subtitulo">
          {marca && <img src="/logo-transparente.png" alt="Juco" className="auth-logo-mini" />}
          {marca ? 'Powered by Juco' : 'Tu ecosistema saludable'}
        </p>
      </div>

      <div className="auth-card">
        {invitacionPendiente && (
          <p className="auth-invitacion-banner">
            Tenés una invitación pendiente — {modo === 'registro' ? 'registrate' : 'ingresá'} para unirte.
          </p>
        )}

        {modo !== 'recuperar' && (
          <div className="auth-tabs">
            <button className={`auth-tab ${modo === 'login' ? 'active' : ''}`} onClick={() => { setModo('login'); setError(''); setMensaje('') }}>
              Ingresar
            </button>
            <button className={`auth-tab ${modo === 'registro' ? 'active' : ''}`} onClick={() => { setModo('registro'); setError(''); setMensaje('') }}>
              Registrarse
            </button>
          </div>
        )}

        {modo === 'recuperar' ? (
          <div className="auth-form">
            <div className="auth-campo">
              <label>Email</label>
              <input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRecuperar()}
              />
            </div>

            {error && <p className="auth-error">{error}</p>}
            {mensaje && <p className="auth-mensaje">{mensaje}</p>}

            <button className="auth-btn" onClick={handleRecuperar} disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar link de recuperación'}
            </button>

            <button
              className="auth-link-olvide"
              style={{ alignSelf: 'center', marginTop: 4 }}
              onClick={() => { setModo('login'); setError(''); setMensaje('') }}
            >
              ← Volver a ingresar
            </button>
          </div>
        ) : (
          <div className="auth-form">
            {modo === 'registro' && (
              <div className="auth-campo">
                <label>Nombre</label>
                <input
                  type="text"
                  placeholder="Tu nombre"
                  value={nombre}
                  onChange={e => setNombre(e.target.value)}
                />
              </div>
            )}

            <div className="auth-campo">
              <label>Email</label>
              <input
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
              />
            </div>

            <div className="auth-campo">
              <label>Contraseña</label>
              <div className="auth-campo-password">
                <input
                  type={mostrarPassword ? 'text' : 'password'}
                  placeholder={modo === 'registro' ? 'Mínimo 6 caracteres' : '••••••••'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (modo === 'login' ? handleLogin() : handleRegistro())}
                />
                <button
                  type="button"
                  className="auth-toggle-password"
                  onClick={() => setMostrarPassword(v => !v)}
                  aria-label={mostrarPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  tabIndex={-1}
                >
                  {mostrarPassword ? <EyeOff size={18} strokeWidth={1.8} /> : <Eye size={18} strokeWidth={1.8} />}
                </button>
              </div>
            </div>

            {modo === 'login' && (
              <button
                className="auth-link-olvide"
                onClick={() => { setModo('recuperar'); setError(''); setMensaje('') }}
              >
                ¿Olvidaste tu contraseña?
              </button>
            )}

            {error && <p className="auth-error">{error}</p>}
            {mensaje && <p className="auth-mensaje">{mensaje}</p>}

            <button
              className="auth-btn"
              onClick={modo === 'login' ? handleLogin : handleRegistro}
              disabled={loading}
            >
              {loading ? 'Cargando...' : modo === 'login' ? 'Ingresar' : 'Crear cuenta'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
